<template>
    <div class="AgentTest" style="position: relative">
        <!--Stop chat-->
        <div class="centerXY btn-stop-chat" id="btn_stop_chat_session" v-if="!isStopChatSession && !loading"
             @click="stopChatSession">
            <div class="icon-stop-chat"></div>
            {{ $f.tc('stopChatSession') }}
        </div>

        <!--List variables-->
        <div class="user-editor" :class="{active: showEditor}">
            <div class="display-flex align-center mb-12" style="font-family: 'open sans bold',serif; font-size: 16px">
                {{ $f.tc('customerProperties') }}
                <div @click="resetUserAttribute" class="btn-icon ml-6">
                    <v-icon size="25" color="#727885">mdi-refresh</v-icon>
                </div>
            </div>
            <ApiParameterEditor
                :items.sync="parameterList"
                :editable="true"
                key-label="name"
                value-label="value"
            ></ApiParameterEditor>
        </div>

        <!--Setting Preview-->
        <div class="user-editor" :class="{active: showSettingPreview}">
            <div
                class="display-flex align-center mb-12"
                style="font-family: 'open sans bold',serif; font-size: 16px; position: relative"
            >
                {{ $f.tc('settingPreview') }}
                <div
                    class="btn-icon-2 btn-close"
                    @click="openSettingPreview"
                    :title="$f.tc('close')"
                    style="position: absolute;right: 0"
                >
                    <img src="/img/scenario/close.svg" alt="icon-close" style="transform: scale(0.9)">
                </div>
            </div>
            <v-tabs-slider color="#f0f0f0"></v-tabs-slider>
            <div style="width: 95%;padding-left: 5px">
                <div class="label" style="margin: 12px 0 6px 0 !important;">{{ $f.tc('voice') }}</div>
                <v-select
                    hide-details outlined
                    v-model="voice"
                    :items="voices"
                    item-text="name"
                    item-value="id"
                    class="input-custom"
                >
                    <template v-slot:item="{ item }">
                        <v-tooltip v-if="item.description" top>
                            <template v-slot:activator="{ on }">
                                <img v-if="item.description"
                                     src="/img/more_detail.svg"
                                     alt="icon-more-detail" style="width: 15px;">
                            </template>
                            <span>{{ item.description }}</span>
                        </v-tooltip>
                        <span :class="item.description ? 'pl-6' : ''">{{ item.name }}</span>
                    </template>
                    <template v-slot:selection="{ item }" class="centerXY">
                        <img v-if="item.description" :title="item.description"
                             src="/img/more_detail.svg"
                             alt="icon-more-detail" style="width: 15px;">
                        <span :class="item.description ? 'pl-6' : ''">{{ item.name }}</span>
                    </template>
                </v-select>
                <div class="label" style="margin-top: 12px">{{ $f.tc('voiceSpeed') }}</div>
                <v-layout align-center>
                    <div class="slider-value">{{ voiceSpeed }}</div>
                    <v-slider v-model="voiceSpeed" min="0.5" max="2" step="0.1"></v-slider>
                </v-layout>
            </div>
        </div>

        <!--List messages-->
        <div class="message-scroll fci-scrollbar" :class="isStopChatSession ? '' : 'pb-36'" ref="MessageScroll">
            <div v-for="(item, i) in messageList" :key="i" class="mb-12">
                <div
                    class="message-container"
                    :class="{user: item.type === 'user', bot: item.type === 'bot'}"
                    style="align-items: center"
                >
                    <div v-if="item.type === 'bot'" class="message-icon-container">
                        <img src="/img/scenario/process_active.svg" alt="bot">
                    </div>
                    <div
                        :class="item.type"
                        class="message"
                        @click="updateTestDataToView(item.dataTest, item.type)"
                    >{{ item.message }}
                        <div class="centerY mt-6" v-if="item.audio && item.type === 'bot'">
                            <audio
                                :src="item.audio"
                                controls="controls"
                                :ref="'audioBot' + i"
                                class="audio-custom"
                                style="width: 278px !important;"
                            ></audio>
                            <div class="btn-icon btn-close" @click="closeAudioPreview(item)" :title="$f.tc('close')">
                                <img src="/img/scenario/close.svg" alt="icon-close" style="transform: scale(0.9)">
                            </div>
                        </div>
                    </div>
                    <div>
                        <div
                            v-if="item.type === 'bot'
                            && !['-----end_call-----','-----transfer_call-----'].includes(item.message)
                            && !canPlayAudio(item.audioLink)
                            && !item.audio"
                        >
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <div class="btn-icon" v-if="!item.loadingAudio" v-on="on">
                                        <img
                                            src="/img/scenario/audio.svg"
                                            alt="icon-get-audio"
                                            @click.stop="getAudio(item, i)"
                                            style="width: 16px"
                                        >
                                    </div>
                                </template>
                                <span>{{ $f.tc('playAudio') }}</span>
                            </v-tooltip>
                            <v-icon v-show="item.loadingAudio" class="tm-spin">mdi-loading</v-icon>
                        </div>
                    </div>
                    <div
                        v-if="item.type === 'user'"
                        @click="reRunCurrentTest(i, false)"
                        title="Rerun to this step"
                        class="btn-icon"
                    >
                        <img src="/img/scenario/chat-forward-fill.svg" alt="icon-rerun-step">
                    </div>
                </div>
                <div class="debug" :class="item.type">
                    <div v-if="item.type === 'bot' && item.ttsWarning"
                         style="font-style: italic;color: #aa2222; width: 305px; overflow-wrap: break-word; white-space: normal !important;">
                        {{ $f.tc('longSentenceWarning') }}
                    </div>
                </div>
            </div>
            <div class="message-container justify-end align-center" v-if="showEndCall">
                <div
                    @click="reRunCurrentTest(null, true)"
                    title="Rerun to this step"
                    class="btn-icon"
                >
                    <img src="/img/scenario/chat-forward-fill.svg" alt="icon-rerun-step-end-call">
                </div>
                <div class="message user">-----end_call-----</div>
            </div>
            <v-layout justify-center v-if="loading">
                <v-icon class="tm-spin">mdi-loading</v-icon>
            </v-layout>
            <div id="audioRecord" class="audio-wave"></div>
        </div>

        <!--Form send message-->
        <v-layout class="AgentTestFooter">
            <!--List actions for chat-->
            <div class="AgentTestMenu">
                <!--Customer Property-->
                <v-tooltip top>
                    <template v-slot:activator="{on}">
                        <div @click="toggleEditor" class="btn-icon-2 mr-6" v-on="on">
                            <img src="/img/scenario/tag.svg" alt="icon-customer-property"
                                 style="transform: rotate(-90deg) scale(1.2)">
                        </div>
                    </template>
                    <span>{{ $f.tc('customerProperty') }}</span>
                </v-tooltip>

                <!--Reset Chat-->
                <v-tooltip top>
                    <template v-slot:activator="{on}">
                        <div @click="resetChatTest" class="btn-icon-2 mr-6" v-on="on">
                            <v-icon size="25" color="#727885">mdi-refresh</v-icon>
                        </div>
                    </template>
                    <span>{{ $f.tc('reset') }}</span>
                </v-tooltip>

                <!--Rerun Chat Test-->
                <v-tooltip top>
                    <template v-slot:activator="{on}">
                        <div @click="reRunCurrentTest(null, isStopChatSession)" class="btn-icon-2 mr-6" v-on="on">
                            <img src="/img/scenario/chat-forward.svg" alt="icon-rerun-test"
                                 style="transform: rotate(-90deg) scale(1.2)">
                        </div>
                    </template>
                    <span>{{ $f.tc('reRunCurrentTest') }}</span>
                </v-tooltip>
            </div>

            <!--Text box chat-->
            <input
                ref="TextBoxChat"
                id="input_chat_test"
                v-model="message"
                @keyup.enter="submit($event)"
                :placeholder="$f.tc('enterMessage')"
                :disabled="!isAllowEnterMsg"
            >

            <!--Menu chat-->
            <v-menu>
                <template v-slot:activator="{ attrs, on }">
                    <div class="btn-icon-2 btn-menu mr-8" v-bind="attrs" v-on="on">
                        <img src="/img/scenario/menu.svg" alt="icon-menu"
                             style="transform: scale(1.05)">
                    </div>
                </template>
                <v-list>
                    <v-list-tile
                        v-for="action in listTestActions"
                        :key="action.id"
                        @click="onAction(action.id)"
                    >
                        <v-list-tile-title
                            v-text="action.text"
                        ></v-list-tile-title>
                    </v-list-tile>
                </v-list>
            </v-menu>
        </v-layout>
    </div>
</template>

<script>
import DialogPromiseMixin from '@/modules/fptai/mixins/DialogPromiseMixin'
import ApiParameterEditor from '@/views/Scenario/NodeEditor/common/ApiParameterEditor'
import ScenarioService from '@/services/scenario.service'
import AudioRecordingMixin from '@/modules/fptai/mixins/AudioRecordingMixin'
import {mapState} from 'vuex'
import CampaignService from '@/services/campaign.service'
import AgentService from "@/services/agent.service";

const {NodeType} = ScenarioService
const TEST_ACTIONS = {
    ADD_TEST_CASE: 'ADD_TEST_CASE',
    PASTE_CALL_ID: 'PASTE_CALL_ID',
    PASTE_HISTORY: 'PASTE_HISTORY',
    SETTING_PREVIEW: 'SETTING_PREVIEW'
}

export default {
    name: 'AgentTest',
    mixins: [DialogPromiseMixin, AudioRecordingMixin],
    components: {ApiParameterEditor},
    data() {
        return {
            TEST_ACTIONS,
            message: '',
            messageList: [],
            showEditor: false,
            parameterList: {},
            savedVariableJSON: {},
            callId: '',
            loading: false,
            savedUserMessageList: [],
            mode: 'manual',
            userMessageIndex: 0,
            voice: 'banmai',
            voices: [],
            voiceSpeed: '1',
            showSettingPreview: false,
            isAllowEnterMsg: true,
            isStopChatSession: false,
            isStopChatBefore: false, // biến để check xem trước khi rerun hoặc chạy lại chat đã chạy stop chat session chưa?
            listTestActions: [
                {
                    id: TEST_ACTIONS.SETTING_PREVIEW,
                    text: this.$f.tc('settingPreview')
                }
            ]
        }
    },
    created() {
        this.getVoices()
    },
    computed: {
        ...mapState({
            variableList: state => state.agent.variable,
        }),
        showEndCall() {
            return !this.loading && this.isStopChatSession
        },
    },
    methods: {
        getVoices() {
            CampaignService.getVoices().then(res => {
                if (res.data.length) {
                    this.voices = res.data
                    this.voices.forEach(item => {
                        if (item.default) {
                            this.voice = item.id
                        }
                    })
                }
            })
        },
        submit(keyEvent) {
            if (keyEvent.keyCode === 13) {
                this.sendMessage('text')
            }
        },
        scrollBottomChat() {
            this.$nextTick(() => {
                let el = this.$refs.MessageScroll
                el.scrollTo({
                    top: el.scrollHeight,
                    behavior: 'smooth'
                })
            })
        },
        stopChatSession(noTransferCall = true) {
            // check chat have end_call or transfer_call
            if (this.showEndCall && noTransferCall) {
                return new Promise(resolve => {
                    resolve('Stop chat session successfully')
                })
            }

            this.isStopChatSession = true

            return AgentService.sendMessage(
                this.$route.params.agentId,
                this.callId,
                "end_call",
                "",
                this.parameterList
            ).then(() => {
                this.message = ''
                this.isAllowEnterMsg = false
                this.scrollBottomChat()
            })
        },
        sendMessage(messageType) {
            let newMessage = this.message
            this.message = ''
            this.addMessage({
                type: 'user',
                message: newMessage,
            })
            this.loading = true
            this.isAllowEnterMsg = false
            let userAttributes = {}
            if (messageType === 'welcome') {
                userAttributes = this.parameterList
            }
            return AgentService.sendMessage(
                this.$route.params.agentId,
                this.callId,
                messageType,
                newMessage,
                userAttributes
            ).then(async res => {
                    const quickReactionContent = res.data?.quick_reaction_content
                    let message = ''
                    if (quickReactionContent) {
                        message += quickReactionContent.trim() + "\n\n"
                    }
                    const nextAction = res.data?.next_action

                    let data_collection = res.data?.data_collection
                    let dataTest = this.savedVariableJSON

                    if (data_collection) {
                        for (let key in data_collection) {
                            if (!(key in this.savedVariableJSON)) {
                                continue
                            }

                            dataTest[key] = {
                                data: data_collection[key],
                                isDataCollection: this.savedVariableJSON[key].isDataCollection
                            }
                        }
                    }

                    const sortedDataTest = Object.keys(dataTest)
                        .sort()
                        .reduce((acc, key) => {
                            acc[key] = dataTest[key];
                            return acc;
                        }, {});
                    message += nextAction?.content?.text
                    this.addMessage({
                        type: 'bot',
                        message: message,
                        dataCollection: res.data?.data_collection,
                        dataTest: sortedDataTest
                    })
                    await this.handleNextAction(nextAction)
                }
            ).finally(() => {
                this.loading = false
                this.isAllowEnterMsg = true
                this.focusTextBoxChat()
            })
        },
        async handleNextAction(nextAction) {
            switch (nextAction.type) {
                case 'waiting_for_user_response': {
                    if (this.mode !== 'auto') return
                    if (this.userMessageIndex < this.savedUserMessageList.length - 1) {
                        this.userMessageIndex++
                        this.message = this.savedUserMessageList[this.userMessageIndex]
                        await this.sendMessage('text')
                    }
                    break
                }
                case 'end_call': {
                    await this.stopChatSession()
                    return
                }
                case 'transfer_call': {
                    this.addMessage({
                        type: 'bot',
                        message: `-----transfer_call-----`,
                    })
                    await this.stopChatSession(false)
                    return
                }
                default: {
                    await this.stopChatSession()
                }
            }
        },
        addMessage(message) {
            this.messageList.push(message)
            this.scrollBottomChat()
        },
        start() {
            this.isStopChatSession = false
            this.showEditor = false
            this.showSettingPreview = false
            this.callId = 'test_' + crypto.randomUUID()
            this.messageList = []
            this.message = 'Start conversation'
            this.sendMessage('welcome')
        },
        toggleEditor() {
            this.showEditor = !this.showEditor
            this.showSettingPreview = false
        },
        resetChatTest() {
            this.$emit('change-visibility', false)
            this.stopChatSession().then(() => {
                this.isStopChatBefore = false
                this.start()
            })
        },
        reRunCurrentTest(messageIndex, isStopChat) {
            this.$emit('change-visibility', false)
            this.isStopChatBefore = isStopChat
            this.stopChatSession().then(() => {
                if (messageIndex === null || messageIndex === undefined) {
                    messageIndex = this.messageList.length - 1
                }
                this.savedUserMessageList = this.messageList.slice(0, messageIndex + 1)
                    .filter(message => message.type === 'user')
                    .map(message => message.message)
                this.mode = 'auto'
                this.userMessageIndex = 0
                this.isStopChatSession = false
                this.start()
            })
        },
        onAction(idAction) {
            switch (idAction) {
                case TEST_ACTIONS.SETTING_PREVIEW:
                    this.openSettingPreview()
                    break
            }
        },
        resetUserAttribute() {
            let data = {}
            this.variableList.value.forEach(item => data[item.name] = item.default_value)
            this.parameterList = data
        },
        closeAudioPreview(item) {
            item.audio = null
            this.$forceUpdate()
        },
        openSettingPreview() {
            this.showSettingPreview = !this.showSettingPreview
            this.showEditor = false
        },
        validURL(str) {
            let pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
                '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
                '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
                '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
                '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
                '(\\#[-a-z\\d_]*)?$', 'i') // fragment locator
            return !!pattern.test(str)
        },
        canPlayAudio(audioLink) {
            if (this.validURL(audioLink)) {
                let ext = audioLink.split(/[#?]/)[0].split('.').pop().trim()
                if (ext === 'wav' || ext === 'mp3') {
                    return true
                }
            }
            return false
        },
        focusTextBoxChat() {
            if (!this.isAllowEnterMsg) return
            this.$nextTick(() => {
                this.$refs.TextBoxChat.focus()
            })
        },
        approximateTtsTime(prompts) {
            let promptParts = prompts.split('.')
            if (promptParts.length < 2) {
                return false
            }
            let currentVoice = this.voices.find(i => i.id === this.voice)
            let ttsGenSpeed = currentVoice['tts_gen_speed']
            let ttsReadSpeed = currentVoice['tts_read_speed']
            if (ttsGenSpeed === 0 || ttsReadSpeed === 0) {
                return false
            }
            let firstPrompt = promptParts[0]
            let secondPrompt = promptParts[1]
            let z1 = (firstPrompt.split(' ').length * ttsReadSpeed) / 1000
            let z2 = secondPrompt.length / ttsGenSpeed
            return z2 > z1;

        },
        updateTestDataToView(data, dataType) {
            this.$emit('update-test-var', data, dataType)
        }
    },
    watch: {
        variableList: {
            immediate: true,
            deep: true,
            handler: function () {
                if (this.variableList) {
                    let data = {}
                    let savedData = {}
                    this.variableList.value.forEach(item => {
                        data[item.name] = item.default_value
                        savedData[item.name] = {
                            data: item.default_value,
                            isDataCollection: item['should_save']
                        }
                    })
                    this.parameterList = {
                        ...this.parameterList,
                        ...data
                    }
                    this.savedVariableJSON = {
                        ...this.savedVariableJSON,
                        ...savedData
                    }
                }
            }
        },
        messageList: {
            deep: true,
            handler: function (newMessageList) {
                if (newMessageList.length > 0) {
                    let lastIndex = newMessageList.length - 1
                    let lastMessage = newMessageList[lastIndex]
                    if (lastMessage.type === 'bot' && lastMessage.message) {
                        lastMessage.ttsWarning = this.approximateTtsTime(lastMessage.message)
                    }
                }
                if (newMessageList.find(i => i.message === '-----end_call-----')) {
                    this.isStopChatSession = true
                    this.scrollBottomChat()
                }
            }
        },
    }
}
</script>

<style scoped lang="scss">
@import "../Scenario/chat.scss";

.AgentTest {
    z-index: 4;
    height: 100%;
    background: #fff;

    .btn-icon-2 {
        border-radius: 50%;
    }

    .btn-icon-2:hover {
        background: #EEF3FC !important;
    }

    .btn-close {
        margin-left: 6px;

        &:hover {
            background: #FFFFFF !important;
        }
    }

    .btn-send, .btn-menu {
        width: 34px !important;
        height: 34px !important;
    }

    .audio-custom {
        height: 40px;
        width: 312px;
        border: 1px solid #FFFFFF;
        border-radius: 0 12px 12px 12px;
    }

    .btn-stop-chat {
        position: absolute;
        bottom: 67px;
        right: 19px;
        border: 1px solid #4D6487;
        padding: 3px 12px 3px 8px;
        border-radius: 5px;
        font-family: "open sans", serif;
        font-size: 13px;
        user-select: none;
        cursor: pointer;
        z-index: 1;
        background: #ffffffcf;

        .icon-stop-chat {
            width: 24px;
            height: 24px;

            &:before {
                content: url("/img/scenario/stop_chat.svg");
            }
        }

        &:hover {
            transition: 0.3s;
            color: #0052CC;
            border: 1px solid #0052CC;

            .icon-stop-chat:before {
                content: url("/img/scenario/stop_chat_blue.svg");
            }
        }
    }

    .audio-custom {
        height: 40px;
        border: 1px solid #FFFFFF;
        border-radius: 0 12px 12px 12px;
    }

    .message {
        max-width: 80% !important;
        flex-direction: column;
        align-items: start;
        white-space: pre-line;
    }
}

.message-scroll {
    height: calc(100% - 55px);
    overflow: auto;
    padding: 12px;
    position: relative;

    .audio-wave {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        overflow: hidden;

        ::v-deep .wave {
            overflow: hidden !important;
        }
    }
}

.user-editor {
    position: absolute;
    top: 0;
    left: calc(10px - 100%);
    width: 100%;
    height: calc(100% - 55px);
    background: #fff;
    transition: all 0.5s ease;
    opacity: 0;
    padding: 12px;
    overflow: auto;
    z-index: 1;

    &.active {
        left: 0;
        opacity: 1;
    }
}

.debug {
    white-space: nowrap;
    color: #9BA3AE;

    &.user {
        padding-right: 10px;
        margin-top: 3px;
        text-align: right;
    }

    &.bot {
        margin-top: 3px;
        padding-left: 60px;
    }
}

.AgentTestHeader {
    background: #0052CC;
    color: #fff;
    font-size: 18px;
    height: 40px;
    padding: 5px 5px 5px 15px;
}

.AgentTestMenu {
    padding: 0 0 0 6px;
    height: 44px;
    line-height: 44px;
    display: flex;
    align-items: center;

    ::v-deep .v-icon {
        color: #9BA3AE;
    }
}

.AgentTestFooter {
    border-top: 1px solid #e4edfa;
    height: 54px;
    line-height: 54px;
    display: flex;
    align-items: center;
    color: #4D6487;

    .menu, .send {
        width: 50px;
        height: 100%;
        color: #F7722F;
    }

    input {
        flex: 1;
        font-size: 14px;
        font-family: "open sans", serif;
        background: #EEF3FC;
        color: #4D6487;
        margin-right: 6px;
        border-radius: 20px;
        height: 38px;
        line-height: 38px;
        padding: 0 12px;

        &::placeholder {
            color: #4D6487;
        }

        &:focus {
            outline: none;
        }
    }
}

.slider-value {
    font-size: 14px;
    width: 44px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #EDEDF2;
    border-radius: 5px;
    margin-right: 15px;
}

.input-custom {
    ::v-deep {
        .v-input__slot {
            background: #FFFFFF;
            border-radius: 5px;
            border: 1px solid #D9D9D9;
            outline: none;
            font-size: 14px;
            margin: 0;
            display: flex;
            align-items: center;
            position: relative;
            height: 32px;
            padding: 6px 12px;

            &:before, &:after {
                border: none !important;
            }
        }
    }
}
</style>
